<template>
  <div>
    <div v-if="minimalist">
      <BaseLink
        type="button"
        class="file-upload__button"
        :disabled="disabled"
        @click.native="disabled ? '' : selectFiles()"
        isSmall
      >
        <BaseIcon name="plus" class="file-upload__add-icon" />
        {{ $t('file-upload.add-more') }}
      </BaseLink>
    </div>
    <div
      v-show="!minimalist"
      ref="uploadContainer"
      class="file-upload"
      :class="{
        'file-upload--document-hover': fileDragActive,
        'file-upload--small': displaySmall,
        'file-upload--horizontal': displayHorizontal,
        disabled,
      }"
      tabindex="0"
      @drop.prevent="dropHandler"
      @dragenter.prevent="disabled ? '' : dragEnter()"
      @dragover.prevent="disabled ? '' : dragEnter()"
      @dragleave.prevent="disabled ? '' : dragLeave()"
      @change="disabled ? '' : handleFileUpload()"
      @click="disabled ? '' : selectFiles()"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <div v-if="displayHorizontal" v-show="!fileDragActive" class="file-upload__inner">
        <div class="file-upload__content">
          <div class="file-upload__row">
            <BaseCircularLoadingBar v-if="isLoading" :size="16" :width="2" />
            <BaseIcon v-else :name="icon" class="file-upload__icon" />
            <h2 class="file-upload__title" :class="{ disabled }">
              {{ displaySmall ? $t('file-upload.add-more') : title }}
            </h2>
          </div>
          <div class="file-upload__row">
            <div class="file-upload__subtitle">{{ description }} {{ $t('file-upload.or') }}</div>
            <input
              type="file"
              ref="filePicker"
              :multiple="multiple"
              size="50"
              class="file-upload__input"
              :accept="allowedFiles"
            />
            <form-button class="file-upload__button" @click.stop.prevent="selectFiles" isSmall :disabled="disabled"
              >{{ $t('file-upload.choose') }}
            </form-button>
          </div>
        </div>
      </div>
      <div v-else class="file-upload__inner">
        <BaseIcon :name="icon" class="file-upload__icon" />
        <div class="file-upload__content" v-show="!fileDragActive">
          <h2 class="file-upload__title" :class="{ disabled }">
            {{ displaySmall ? $t('file-upload.add-more') : title }}
          </h2>
          <div class="file-upload__row">
            <div class="file-upload__subtitle">{{ $t('file-upload.upload') }}</div>
            <div class="file-upload__subtitle">{{ $t('file-upload.or') }}</div>
            <input
              type="file"
              ref="filePicker"
              :multiple="multiple"
              size="50"
              class="file-upload__input"
              :accept="allowedFiles"
            />
            <form-button class="file-upload__button" @click.stop.prevent="selectFiles" isSmall :disabled="disabled"
              >{{ $t('file-upload.choose') }}
            </form-button>
          </div>
        </div>
      </div>
      <div class="file-upload__content" v-show="fileDragActive">
        <h2 class="file-upload__title">{{ $t('file-upload.drop') }}</h2>
      </div>
    </div>
  </div>
</template>

<script>
import BaseLink from '@/components/base/BaseLink';
import BaseIcon from '@/components/base/BaseIcon';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import FormButton from '@/components/form/FormButton';

export default {
  name: 'FileUpload',
  components: {
    BaseLink,
    BaseIcon,
    BaseCircularLoadingBar,
    FormButton,
  },
  props: {
    icon: {
      type: String,
      default: 'upload-cloud',
    },
    title: {
      type: String,
      default() {
        return this.$t('file-upload.add');
      },
    },
    description: {
      type: String,
      default() {
        return this.$t('file-upload.upload');
      },
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    displaySmall: {
      type: Boolean,
      default: false,
    },
    displayHorizontal: {
      type: Boolean,
      default: false,
    },
    minimalist: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowedFiles: {
      type: String,
      default: '.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  },
  data() {
    return {
      fileDragActive: false,
      isHovered: false,
    };
  },
  mounted() {
    window.addEventListener('paste', this.handlePaste);
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    dropHandler(e) {
      if (this.disabled) return;
      this.fileDropped(e);
    },
    handleFileUpload() {
      const files = Array.from(this.$refs.filePicker.files);
      this.$emit('add-files', files);
      this.$refs.filePicker.value = '';
    },
    selectFiles() {
      if (this.$refs.filePicker) {
        this.$refs.filePicker.click();
      }
    },
    fileDropped(event) {
      const { files } = event.dataTransfer;
      this.$emit('add-files', files);
      this.fileDragActive = false;
    },
    dragEnter() {
      this.fileDragActive = true;
    },
    dragLeave() {
      this.fileDragActive = false;
    },
    handlePaste(event) {
      if (this.disabled || !this.isHovered) return;

      const items = event.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        event.preventDefault();
        this.$emit('add-files', files);
      }
    },
  },
};
</script>

<style lang="scss">
.file-upload {
  align-items: center;
  border: 1px dashed $color-neutral;
  border-radius: $border-radius-root;
  display: flex;
  min-height: 242px;
  justify-content: center;
  cursor: pointer;
  padding: 16px;
  // Pour animation entre normal et Small
  right: -200px;
  transition: right ease-out 400ms, background 200ms ease, box-shadow 200ms ease;
  width: 100%;
  color: $color-neutral;
  outline: none;

  &:focus-visible {
    outline: 2px solid $color-normal;
    outline-offset: 2px;
  }

  &:hover {
    border-color: $color-normal;
    color: $color-normal;
    transition: all 200ms ease;
    background: $color-neutral-pale;

    &.disabled {
      border-color: $color-neutral;
    }
  }

  &.disabled {
    background: $color-neutral-pale;
  }

  &--document-hover {
    background: $color-white;
    box-shadow: $shadow-default;
    border-color: transparent;
    // Règle le probleme que dragLeave est callé chaque fois qu'on est sur une élément enfant
    & * {
      pointer-events: none;
    }
  }

  &__inner {
    padding: 16px;
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__icon {
    color: $color-neutral;
    flex: none;
    height: 60px;
    width: 60px;
  }

  &__content {
    flex: auto;
  }

  &__title {
    color: $color-neutral;
    font-size: 16px;
    text-transform: uppercase;
    font-weight: bold;
    margin: 0 0 8px;
    text-align: center;

    &.disabled {
      color: $color-neutral;
    }
  }

  &__row {
    align-items: center;
    flex-direction: column;
    display: flex;
    gap: 8px;
  }

  &__subtitle {
    color: $color-neutral;
    font-size: 14px;
  }

  &__input {
    display: none;
  }

  &__button {
    margin: 0;
    margin-left: 8px;
    width: max-content;
  }

  &__add-icon {
    height: 14px;
    width: 14px;
    margin-right: 4px;
  }

  // Affichage Small, en position fixed
  &--small {
    bottom: 0;
    display: block;
    height: 126px;
    padding: 32px 16px 16px;
    right: 0;
    z-index: 5;

    .file-upload__inner {
      align-items: flex-start;
      width: 100%;
    }

    .file-upload__icon {
      height: 34px;
      margin-right: 8px;
      width: 34px;
    }

    .file-upload__row {
      display: block;
    }

    .file-upload__title {
      font-size: 12px;
    }

    .file-upload__subtitle {
      display: block;
      font-size: 12px;
      margin-bottom: 8px;
    }

    .file-upload__button {
      margin: 0;
    }
  }

  &--horizontal {
    width: 100%;
    min-height: 105px;
    padding: 0px;

    .file-upload__inner {
      width: 100%;
    }

    .file-upload__title {
      margin-bottom: 0;
    }

    .file-upload__row:first-child {
      flex-direction: row;
      gap: 4px;
      margin-bottom: 4px;
    }

    .file-upload__icon {
      height: 20px;
      width: 20px;
    }
  }
}

@media screen and (min-width: 768px) {
  .file-upload {
    &--small {
      border-radius: $border-radius-large 0 0 0;
      border-right: none;
      border-bottom: none;
      position: fixed;
      width: 224px;
    }
  }
}
</style>
